/* Imports libraries */
import "slick-carousel";

/* Import helper functions */
import detectBrowser from "./helpers/detect-browser";
import FocusOutline from "./helpers/focus";

/* Imports modules */
import Checkbox from "../../components/atoms/checkbox/checkbox";
import ExtendedToggle from "../../components/atoms/extended-toggle/extended-toggle";
import CategorySelector from "../../components/molecules/category-selector/category-selector";
import Toggle from "../../components/atoms/toggle/toggle";
import Navigation from "../../components/molecules/navigation/navigation";
import StickyHeader from "../../components/organisms/header/header-sticky/header-sticky";
import Header from "../../components/organisms/header/header/header";
import Footer from "../../components/organisms/footer/footer";
import CustomSelect from "../../components/organisms/custom-select/custom-select";
import RelatedArticles from "../../components/molecules/related-articles/related-articles/related-articles";
import ProductPuff from "../../components/molecules/product-puff/product-puff/product-puff";
import iframeResizer from "../../components/molecules/iframe-container/iframe-container";
import ContactForm from "../../components/organisms/contact-form/contact-form";
import heroWithVideoText from "../../components/molecules/hero/hero-with-video-text/hero-with-video-text";
import expenseManagerVideo from "../../components/molecules/expense-manager-video/expense-manager-video";
import FeedbackForm from "../../components/organisms/feedback-form/feedback-form";
import ToggleLoginOption from "../../components/organisms/login-block/login-block";
import AlertCookie from "../../components/molecules/alert-block/alert-block";
import ProductCard from "../../components/molecules/product-card/product-card";
import initialOverlay from "../../components/atoms/initial-overlay/initial-overlay";
/* Not sure what this CrossDomainTracking is, used with webpack earlier,
if it's needed we add it back, but needs to be rebuilt for vite then. */
import CrossDomainTracking from "../../components/atoms/cross-domain-tracking/cross-domain-tracking";
import marketSelectorBar from "../../components/molecules/market-selector-bar/market-selector-bar";
import marketSelectorStyled from "../../components/molecules/market-selector-styled/market-selector-styled";
import accordionOpenFeedback from "../../components/molecules/accordion/accordion-faq/accordion-faq.js";
import leadGeneration from "../../components/molecules/lead-generation/lead-generation";
import leadGeneration2 from "../../components/molecules/lead-generation-v2/lead-generation-v2";
import leadArea from "../../components/molecules/lead-area/lead-area";
import NotificationBar from "../../components/molecules/notification-bar/notification-bar";
// import CookieConsentV2 from '../../components/organisms/cookie-consent/cookie-consent';
import CookieConsentV2Dialog from "../../components/organisms/cookie-consent/cookie-consent-dialog";
import Tabs from "../../components/organisms/tabs/tabs";

const animateWhenVisible = () => {
  if (typeof IntersectionObserver !== "function") {
    return;
  }
  const animateElements = document.querySelectorAll(
    ".animate-on-visible__do-animate",
  );
  animateElements.forEach((animateElement) => {
    let threshold = 0.5;
    if (animateElement.dataset.animationThreshold) {
      const tryThres = parseFloat(animateElement.dataset.animationThreshold);
      if (isFinite(tryThres)) {
        threshold = tryThres;
      }
    }
    if (animateElement == null) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateElement.classList.add("animate-on-visible__run-animation");
            return observer.unobserve(animateElement);
          }
        });
      },
      {
        threshold,
      },
    );
    observer.observe(animateElement);
  });
};

const loadLazyImages = () => {
  const images = document.querySelectorAll("img[data-src]");

  const showImageAndPicture = (image) => {
    const show = (el, fromAttr, toAttr) => {
      el.setAttribute(toAttr, el.getAttribute(fromAttr));
      el.removeAttribute(fromAttr);
    };
    const picSource = image.previousElementSibling;
    show(picSource, "data-srcset", "srcset");
    show(image, "data-src", "src");
  };

  if (typeof IntersectionObserver !== "function") {
    images.forEach((image) => {
      showImageAndPicture(image);
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const image = entry.target;
        image.previousElementSibling.classList.add("lazy-loaded");
        image.classList.add("lazy-loaded");
        showImageAndPicture(image);
        observer.unobserve(image);
        return;
      }
    });
  });
  images.forEach((image) => {
    observer.observe(image);
  });
};

const onDOMContentLoaded = () => {
  Checkbox();
  ExtendedToggle();
  NotificationBar();
  CategorySelector();
  AlertCookie();
  FeedbackForm();
  ContactForm();
  Header();
  StickyHeader();
  Navigation();
  ToggleLoginOption();
  Toggle();
  FocusOutline();
  heroWithVideoText();
  leadArea();
  leadGeneration();
  leadGeneration2();
  RelatedArticles();
  ProductPuff();
  expenseManagerVideo();
  detectBrowser();

  ProductCard.keepElementsAtEqualHeight(".product-card__item");
  ProductCard.keepElementsAtEqualHeight(".support-links__item");

  const mql = window.matchMedia("(min-width: 768px)");
  const applyEqualProductHeights = () => {
    if (mql.matches) {
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row1 .new-product-card-package__text",
      );
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row1 .new-product-card-package-content",
      );
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row1 .new-product-card-packages__list",
      );
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row2 .new-product-card-package__text",
      );
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row2 .new-product-card-package-content",
      );
      ProductCard.keepElementsAtEqualHeight(
        ".prd-row2 .new-product-card-packages__list",
      );
    } else {
      document
        .querySelectorAll(".prd-row1 .new-product-card-package__text")
        .forEach((el) => (el.style.minHeight = ""));
      document
        .querySelectorAll(".prd-row1 .new-product-card-package-content")
        .forEach((el) => (el.style.minHeight = ""));
      document
        .querySelectorAll(".prd-row1 .new-product-card-packages__list")
        .forEach((el) => (el.style.minHeight = ""));
    }
  };
  mql.addEventListener("change", applyEqualProductHeights);
  applyEqualProductHeights();

  ProductCard.addCardChoiceEventListners();
  new CrossDomainTracking().addCookieValueToLinks();
  marketSelectorBar();
  marketSelectorStyled();
  accordionOpenFeedback();
  // CookieConsentV2();
  CookieConsentV2Dialog();
  Tabs();
  Footer();
  CustomSelect();

  // detect keyboard / screen reader or not
  document.body.classList.add("mouse"); // default
  document.addEventListener("keyup", (e) => {
    if (e.key === "Tab" && !e.altKey) {
      document.body.classList.add("keyboard");
      document.body.classList.remove("mouse");
    }
  });
  const onIsMouse = () => {
    document.body.classList.remove("keyboard");
    document.body.classList.add("mouse");
  };
  document.body.addEventListener("mousemove", onIsMouse, { passive: true });
  document.addEventListener("click", onIsMouse);

  animateWhenVisible();
  loadLazyImages();

  window.addEventListener("message", (event) => {
    if (event.message === "scrollTopOfOA" || event.data === "scrollTopOfOA") {
      var iframe = document.querySelector("#theIFrame");
      var offsetTopOA = 0;
      if (iframe) {
        offsetTopOA = iframe.offsetTop;
      }
      window.scrollTo(0, offsetTopOA);
    }
  });
  $(document).foundation();
  initialOverlay();
};

window.onload = () => {
  iframeResizer();
};

if (typeof document.readyState === "string") {
  // https://stackoverflow.com/a/39993724/846348
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      onDOMContentLoaded();
    });
  } else {
    onDOMContentLoaded();
  }
} else {
  console.error("too old browser");
}

if (import.meta.hot) {
  import.meta.hot.accept();
  if (!window.__hmrEnabled) {
    window.__hmrEnabled = true;
    console.log("HMR JS enabled");
  }
}
